import { Controller } from "@hotwired/stimulus"
import { NativeBridge } from "../lib/native_bridge"
import { pageCovered } from "../lib/overlay"

const COVERED_RECHECK_MS = 400

export default class extends Controller {
  static values = {
    autoDismiss: { type: Boolean, default: true },
    duration: Number,
    exitDuration: Number,
    onceKey: String,
    // Coach-mark mode: show on this window event (or when readySelector already matches, or after showFallback ms),
    // never under the splash or an open modal; leave on dismissOn.
    showOn: String,
    readySelector: String,
    showFallback: Number,
    dismissOn: String,
    // Celebrations: native haptic on show; a tap anywhere ends the cue early (the tap still reaches the page).
    haptic: String,
    dismissOnTap: Boolean
  }

  connect() {
    if (this.shouldSkipOnce()) {
      this.element.remove()
      return
    }

    if (this.hasDismissOnValue) {
      this.boundDismiss = () => this.dismiss()
      window.addEventListener(this.dismissOnValue, this.boundDismiss, { once: true })
    }

    if (this.hasShowOnValue && !this.readyAlready()) {
      this.boundShow = () => this.show()
      window.addEventListener(this.showOnValue, this.boundShow, { once: true })
      if (this.showFallbackValue > 0) this.fallbackTimer = window.setTimeout(this.boundShow, this.showFallbackValue)
    } else {
      this.show()
    }
  }

  disconnect() {
    this.clearTimers()
    this.removeListeners()
  }

  show() {
    if (this.shown || this.dismissed) return
    if (this.hasShowOnValue && this.covered()) {
      this.coveredTimer = window.setTimeout(() => this.show(), COVERED_RECHECK_MS)
      return
    }

    this.shown = true
    this.clearTimers()
    this.element.dataset.motionCueState = "shown"
    this.dispatch("shown")

    requestAnimationFrame(() => {
      this.element.classList.remove("translate-y-2", "opacity-0")
    })

    if (this.hasHapticValue) NativeBridge.Haptic.notification(this.hapticValue)
    if (this.dismissOnTapValue) {
      this.boundTapDismiss = () => this.dismiss()
      window.addEventListener("pointerdown", this.boundTapDismiss, { once: true, passive: true })
    }

    if (this.autoDismissValue) this.armDismissTimer()
  }

  // A coach mark that a modal slides over keeps its full on-screen time instead of expiring unseen.
  armDismissTimer() {
    this.dismissTimer = window.setTimeout(() => {
      if (this.hasShowOnValue && this.covered()) this.armDismissTimer()
      else this.dismiss()
    }, this.durationValue)
  }

  covered() {
    return pageCovered()
  }

  dismiss(event) {
    event?.preventDefault()
    if (this.dismissed) return

    this.dismissed = true
    this.markSeen()
    this.clearTimers()
    this.removeListeners()

    if (!this.shown) {
      this.element.remove()
      return
    }

    this.element.dataset.motionCueState = "leaving"
    this.element.classList.add("translate-y-2", "opacity-0")

    window.setTimeout(() => {
      this.element.remove()
    }, this.exitDurationValue)
  }

  readyAlready() {
    return this.hasReadySelectorValue && document.querySelector(this.readySelectorValue) !== null
  }

  shouldSkipOnce() {
    if (!this.hasOnceKeyValue) return false

    try {
      return window.localStorage.getItem(this.onceKeyValue) === "seen"
    } catch {
      return false
    }
  }

  markSeen() {
    if (!this.hasOnceKeyValue) return

    try {
      window.localStorage.setItem(this.onceKeyValue, "seen")
    } catch {
      // Ignore storage failures; the cue can still be shown safely.
    }
  }

  removeListeners() {
    if (this.boundShow) window.removeEventListener(this.showOnValue, this.boundShow)
    if (this.boundDismiss) window.removeEventListener(this.dismissOnValue, this.boundDismiss)
    if (this.boundTapDismiss) window.removeEventListener("pointerdown", this.boundTapDismiss)
    this.boundShow = null
    this.boundDismiss = null
    this.boundTapDismiss = null
  }

  clearTimers() {
    for (const name of ["dismissTimer", "fallbackTimer", "coveredTimer"]) {
      if (this[name]) window.clearTimeout(this[name])
      this[name] = null
    }
  }
}
